import { toString as renderQrSvg } from 'qrcode';
import * as React from 'react';

import { CONNECT_QR_OPTIONS, qrSvgDataUri } from '../domains/member-in-event/constants';

/**
 * A member token, drawn.
 *
 * ## Why it is in `src/member/` rather than inside the screen that first needed it
 *
 * It was local to `connect-page.tsx` until `MEM-057` gave the member a **second** code — the
 * profile one, shown in a dialog on Your account (ADR 053). Two codes rendered by two copies of
 * this is two error-correction levels and two quiet-zone margins waiting to disagree, which for a
 * QR is not cosmetic: the margin is what makes a code findable at all, and a code that scans on
 * one screen and not the other is a defect nobody reproduces at a desk. One renderer, one
 * {@link CONNECT_QR_OPTIONS}.
 *
 * ## What it does about the two ways this goes wrong
 *
 * Rendering is asynchronous, so it cannot happen during render. The effect is guarded against
 * resolving into an unmounted component and against resolving into a *different* code after a
 * navigation or a replacement — the second one matters more than it looks, because a stale image
 * here is the member showing somebody a code that no longer resolves.
 *
 * A failure sets no source, which the caller shows as its skeleton rather than as a broken image.
 */
export function useQrCodeSource(payload: string | null): string | null {
  const [source, setSource] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (payload === null) {
      setSource(null);
      return;
    }
    let live = true;
    void renderQrSvg(payload, CONNECT_QR_OPTIONS)
      .then((svg) => {
        if (live) setSource(qrSvgDataUri(svg));
      })
      .catch(() => {
        if (live) setSource(null);
      });
    return () => {
      live = false;
    };
  }, [payload]);

  return source;
}
